import { useMemo, useState } from 'react';
import {
  collectCardPostBadgeValues,
  filterCardPrintPosts,
  getCardPostBadgeLabel,
  type CardPrintPost,
} from './useCardPrintPosts';

/**
 * AW6 — 投稿選択パネル (左ペイン)。old2 `PostSelectionPanel.tsx` を v2 に移植。
 *
 * 旧 100 件ハードクリップ (`MAX_DISPLAY_POSTS`) を廃止し、ページング (50/page) で全件到達可能に。
 *
 * 踏襲した点:
 *   - キーワード検索 (タイトル / コメント / 投稿者)
 *   - 絞り込みボタン: カードのバッジの対象項目 (badgeFieldPath) の値から動的生成 (バッジ非表示なら出さない)
 *   - 「AI解析済みのみ」トグル (aiAnalysis.status === 'done')
 *   - ページネーション 50/page + 前/次
 *   - 「ページ全選択 / フィルタ全選択 / 解除」ボタン
 *   - 選択件数 / フィルタ件数 / シート数の表示
 *   - サムネ (resolvedThumbUrls)
 *
 * v2 差分:
 *   - inline style 禁止 (shared-ui Tailwind preset の slate/primary トークンを使用)
 *   - thumbnail は useResolvedPhotoUrls 解決済 URL を上位から受け取る
 */

const PAGE_SIZE = 50;
const PER_SHEET = 10;

/** セッション選択ドロップダウンの 1 項目 (id→name)。CardPrintPage が並び順そのまま渡す。 */
export interface SessionOption {
  id: string;
  name: string;
}

export interface PostSelectionPanelProps {
  posts: CardPrintPost[];
  selectedIds: Set<string>;
  onChange: (ids: Set<string>) => void;
  loading: boolean;
  error?: Error | null;
  /** post.id → 解決済サムネ URL (useResolvedPhotoUrls)。 */
  thumbUrls: Record<string, string | null>;
  /** false なら全操作 disabled (権限なし)。 */
  canSelect: boolean;
  /**
   * セッション選択ドロップダウンの選択肢 (useSessions の並び順のまま)。
   * 空配列ならドロップダウン自体を出さない (セッション未使用地区)。
   */
  sessions?: SessionOption[];
  /**
   * セッション絞り込みを「投稿者の所属セッション」で行うための uid → joinedSessionIds map
   * (useCardPrintParticipants)。投稿者がそのセッションに所属していれば表示する。
   * 未指定時は後方互換で post.sessionId 一致で絞り込む。
   */
  authorSessionsByUid?: Record<string, readonly string[]>;
  /**
   * カードのバッジの対象項目 (postField key、getBadgeFieldPath)。絞り込みボタンはこの項目の値から作る。
   * '' (バッジ非表示) なら絞り込みボタンを出さない。
   */
  badgeFieldPath?: string;
}

export function PostSelectionPanel({
  posts,
  selectedIds,
  onChange,
  loading,
  error,
  thumbUrls,
  canSelect,
  sessions = [],
  authorSessionsByUid,
  badgeFieldPath = '',
}: PostSelectionPanelProps): JSX.Element {
  const [keyword, setKeyword] = useState('');
  const [valueFilter, setValueFilter] = useState<string>('all');
  const [aiOnly, setAiOnly] = useState(false);
  const [sessionFilter, setSessionFilter] = useState<string>('all');
  const [page, setPage] = useState(1);

  const values = useMemo(
    () => (badgeFieldPath ? collectCardPostBadgeValues(posts, badgeFieldPath) : []),
    [posts, badgeFieldPath],
  );
  // バッジの対象項目を切り替えて選択中の値が無くなったら「すべて」に戻す。
  const badgeValueFilter = values.includes(valueFilter) ? valueFilter : 'all';

  const filtered = useMemo(
    () =>
      filterCardPrintPosts(posts, {
        keyword,
        badgeValueFilter,
        badgeFieldPath,
        aiOnly,
        sessionId: sessionFilter,
        authorSessionsByUid,
      }),
    [posts, keyword, badgeValueFilter, badgeFieldPath, aiOnly, sessionFilter, authorSessionsByUid],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // フィルタ変更で totalPages が縮んでも安全なよう、表示・スライスは clamp 済 safePage を使う
  // (stored page は filter 操作時に setPage(1) で巻き戻す)。
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const sheetCount = Math.ceil(selectedIds.size / PER_SHEET);

  function toggle(id: string): void {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  }
  function selectPage(): void {
    const next = new Set(selectedIds);
    for (const p of paginated) next.add(p.id);
    onChange(next);
  }
  function selectAllFiltered(): void {
    const next = new Set(selectedIds);
    for (const p of filtered) next.add(p.id);
    onChange(next);
  }
  function clearAll(): void {
    onChange(new Set());
  }

  const chipBase = 'px-2 py-1 text-xs rounded transition-colors disabled:opacity-40';
  const chipActive = 'bg-primary-600 text-white';
  const chipIdle = 'bg-slate-100 text-slate-700 hover:bg-primary-50';

  return (
    <div className="flex flex-col" data-testid="card-post-selection">
      <div className="grid grid-cols-1 gap-2 mb-2">
        <input
          type="text"
          placeholder="タイトル・コメント・投稿者で検索"
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            setPage(1);
          }}
          disabled={!canSelect}
          aria-label="投稿をキーワード検索"
          className="w-full px-2 py-1 text-sm border border-slate-300 rounded disabled:bg-slate-50"
        />
        {sessions.length > 0 && (
          <div>
            <select
              value={sessionFilter}
              onChange={(e) => {
                setSessionFilter(e.target.value);
                setPage(1);
              }}
              disabled={!canSelect}
              aria-label="セッションで絞り込み"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded bg-white disabled:bg-slate-50"
            >
              <option value="all">すべてのセッション</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || '(名称未設定)'}
                </option>
              ))}
            </select>
            {sessionFilter !== 'all' && (
              <p className="mt-1 text-[10px] text-slate-500">
                選んだセッションに参加している投稿者の投稿を表示します。
              </p>
            )}
          </div>
        )}
        {values.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            <button
              type="button"
              onClick={() => {
                setValueFilter('all');
                setPage(1);
              }}
              disabled={!canSelect}
              className={`${chipBase} ${badgeValueFilter === 'all' ? chipActive : chipIdle}`}
            >
              すべて
            </button>
            {values.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  setValueFilter(c);
                  setPage(1);
                }}
                disabled={!canSelect}
                className={`${chipBase} ${badgeValueFilter === c ? chipActive : chipIdle}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={aiOnly}
            onChange={(e) => {
              setAiOnly(e.target.checked);
              setPage(1);
            }}
            disabled={!canSelect}
          />
          AI解析済みのみ
        </label>
        <p className="text-[11px] text-slate-600" data-testid="card-selection-counts">
          {selectedIds.size} 件選択 / {filtered.length} 件中 → {sheetCount} シート
        </p>
        <div className="flex gap-1 flex-wrap">
          <button
            type="button"
            onClick={selectPage}
            disabled={!canSelect || paginated.length === 0}
            className="px-2 py-1 text-xs bg-slate-100 rounded hover:bg-slate-200 disabled:opacity-40"
          >
            ページ全選択
          </button>
          <button
            type="button"
            onClick={selectAllFiltered}
            disabled={!canSelect || filtered.length === 0}
            className="px-2 py-1 text-xs bg-slate-100 rounded hover:bg-slate-200 disabled:opacity-40"
          >
            フィルタ全選択
          </button>
          <button
            type="button"
            onClick={clearAll}
            disabled={!canSelect || selectedIds.size === 0}
            className="px-2 py-1 text-xs bg-slate-100 rounded hover:bg-slate-200 disabled:opacity-40"
          >
            解除
          </button>
        </div>
      </div>

      <div className="border border-slate-200 rounded max-h-[55vh] overflow-y-auto">
        {loading && <p className="p-3 text-center text-xs text-slate-500">読込中…</p>}
        {error && (
          <p className="p-3 text-center text-xs text-danger-700">読込エラー: {error.message}</p>
        )}
        {!loading && !error && filtered.length === 0 && (
          <p className="p-3 text-center text-xs text-slate-500">投稿がありません。</p>
        )}
        {paginated.map((post) => {
          const checked = selectedIds.has(post.id);
          const catLabel = badgeFieldPath ? getCardPostBadgeLabel(post, badgeFieldPath) : '';
          const thumb = thumbUrls[post.id];
          return (
            <label
              key={post.id}
              className={`flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 last:border-b-0 cursor-pointer ${checked ? 'bg-primary-50' : 'hover:bg-slate-50'}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(post.id)}
                disabled={!canSelect}
              />
              <span
                className="w-10 h-10 bg-slate-200 rounded flex-shrink-0 bg-cover bg-center"
                style={thumb ? { backgroundImage: `url(${thumb})` } : undefined}
                aria-hidden
              />
              <span className="flex-1 min-w-0">
                <span className="text-xs font-medium text-slate-800 truncate block">
                  {post.title || '(無題)'}
                </span>
                <span className="text-[10px] text-slate-500 truncate block">
                  {catLabel ? `${catLabel} ・ ` : ''}
                  {post.createdAt ? post.createdAt.toLocaleDateString('ja-JP') : '日時不明'} ・{' '}
                  {post.authorNickname}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs mt-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={safePage <= 1}
            className="px-2 py-1 bg-slate-100 rounded disabled:opacity-40"
            aria-label="前のページ"
          >
            前
          </button>
          <span className="text-slate-600">
            {safePage} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage >= totalPages}
            className="px-2 py-1 bg-slate-100 rounded disabled:opacity-40"
            aria-label="次のページ"
          >
            次
          </button>
        </div>
      )}
    </div>
  );
}
